'use client';
import React, { useState } from 'react';
import { Eye, EyeOff, Lock, CheckCircle, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

interface SetPasswordFormProps {
  onPasswordSet: () => void;
}

export default function SetPasswordForm({ onPasswordSet }: SetPasswordFormProps) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const passwordStrength = (): { label: string; color: string; width: string } => {
    if (password.length === 0) return { label: '', color: 'bg-border', width: 'w-0' };
    if (password.length < 6) return { label: 'ضعيفة', color: 'bg-red-500', width: 'w-1/4' };
    if (password.length < 8) return { label: 'متوسطة', color: 'bg-amber-500', width: 'w-2/4' };
    if (/[A-Z]/.test(password) && /[0-9]/.test(password)) return { label: 'قوية', color: 'bg-green-500', width: 'w-full' };
    return { label: 'جيدة', color: 'bg-teal-500', width: 'w-3/4' };
  };

  const strength = passwordStrength();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!password) {
      setError('يرجى إدخال كلمة المرور الجديدة.');
      return;
    }
    if (password.length < 8) {
      setError('كلمة المرور يجب أن تكون 8 أحرف على الأقل.');
      return;
    }
    if (password !== confirmPassword) {
      setError('كلمتا المرور غير متطابقتين. يرجى التأكد من التطابق.');
      return;
    }

    setIsLoading(true);
    try {
      const supabase = createClient();

      // Update the password in Supabase Auth
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        setError('تعذّر تحديث كلمة المرور. يرجى المحاولة مجدداً.');
        return;
      }

      // Mark password_set = true via RPC
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.rpc('mark_password_set', { p_user_id: user.id });
      }

      toast.success('تم تعيين كلمة المرور بنجاح — مرحباً بك!');
      onPasswordSet();
    } catch {
      setError('حدث خطأ أثناء تعيين كلمة المرور. يرجى المحاولة مجدداً.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="mb-6 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-teal-50 border border-teal-200 mb-4">
          <ShieldCheck size={28} className="text-teal-600" />
        </div>
        <h2 className="text-2xl font-bold text-foreground">تعيين كلمة المرور</h2>
        <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
          مرحباً بك! هذه أول مرة تسجّل دخولك.<br />
          يرجى تعيين كلمة مرور دائمة لحسابك.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-300 text-red-700 rounded-lg px-4 py-3 text-sm flex items-start gap-2.5">
            <span className="shrink-0 mt-0.5">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {/* New Password */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">
            كلمة المرور الجديدة <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Lock size={16} className="absolute top-1/2 -translate-y-1/2 end-3 text-muted-foreground" />
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => { setPassword(e.target.value); setError(''); }}
              placeholder="8 أحرف على الأقل"
              required
              className="w-full h-11 bg-input border border-border rounded-lg pe-9 ps-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute top-1/2 -translate-y-1/2 start-3 text-muted-foreground hover:text-foreground transition-colors"
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {/* Strength bar */}
          {password.length > 0 && (
            <div className="mt-2">
              <div className="h-1.5 bg-border rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-300 ${strength.color} ${strength.width}`} />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                قوة كلمة المرور: <span className="font-semibold">{strength.label}</span>
              </p>
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-1.5">
            تأكيد كلمة المرور <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Lock size={16} className="absolute top-1/2 -translate-y-1/2 end-3 text-muted-foreground" />
            <input
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={e => { setConfirmPassword(e.target.value); setError(''); }}
              placeholder="أعد إدخال كلمة المرور"
              required
              className={`w-full h-11 bg-input border rounded-lg pe-9 ps-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all ${
                confirmPassword && confirmPassword !== password ? 'border-red-400' : 'border-border'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute top-1/2 -translate-y-1/2 start-3 text-muted-foreground hover:text-foreground transition-colors"
            >
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
            {/* Match indicator */}
            {confirmPassword && confirmPassword === password && (
              <CheckCircle size={16} className="absolute top-1/2 -translate-y-1/2 end-10 text-green-500" />
            )}
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full h-11 teal-gradient text-white rounded-lg font-semibold text-sm btn-primary-scale flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed mt-2"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="30 70" />
              </svg>
              <span>جاري الحفظ...</span>
            </>
          ) : (
            <>
              <ShieldCheck size={16} />
              <span>تعيين كلمة المرور والدخول</span>
            </>
          )}
        </button>
      </form>

      <p className="text-center text-xs text-muted-foreground mt-4">
        ستُستخدم هذه كلمة المرور في جميع عمليات تسجيل الدخول القادمة
      </p>
    </div>
  );
}
